import { useEffect, useRef } from "react";

let scrollLockCount = 0;
let savedBodyOverflow = "";
let savedHtmlOverflow = "";

// Lock both <html> and <body>. In brownfield hosts the page-level scroller is
// the document element (`window` scroll), so locking only <body> leaves the
// background scrollable behind the modal. Locking both covers either scroller.
function acquireScrollLock() {
  if (scrollLockCount === 0) {
    const html = document.documentElement as HTMLElement | undefined;
    savedBodyOverflow = document.body?.style?.overflow ?? "";
    savedHtmlOverflow = html?.style?.overflow ?? "";
    if (document.body?.style) document.body.style.overflow = "hidden";
    if (html?.style) html.style.overflow = "hidden";
  }
  scrollLockCount++;
}

function releaseScrollLock() {
  scrollLockCount--;
  if (scrollLockCount === 0) {
    const html = document.documentElement as HTMLElement | undefined;
    if (document.body?.style) document.body.style.overflow = savedBodyOverflow;
    if (html?.style) html.style.overflow = savedHtmlOverflow;
  }
}

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

/**
 * Clears `inert` on this dialog's body-level portal in case a parent
 * modal's focus trap inerted it. Mirrors MADS `Dialog.web.tsx` (RN-4068).
 */
export function uninertOwnPortal(dialogEl: HTMLElement | null) {
  if (!dialogEl || typeof document === "undefined") return;
  let bodyChild = dialogEl;
  while (bodyChild.parentElement && bodyChild.parentElement !== document.body) {
    bodyChild = bodyChild.parentElement;
  }
  if ((bodyChild as HTMLElement & { inert?: boolean }).inert) {
    (bodyChild as HTMLElement & { inert?: boolean }).inert = false;
  }
}

/**
 * Applies dialog isolation to `dialogEl` and returns a cleanup function.
 *
 * Shared by {@link useModalFocusTrap} (self-contained modals whose dialog
 * element is passed by ref) and {@link useSceneModalFocusTrap} (React
 * Navigation screens whose dialog element is resolved from the scene). When
 * `applyDialogRole` is true, `role="dialog"`/`aria-modal` are set on the
 * element for the duration of the trap and restored on cleanup — used for the
 * navigation case, where the dialog element is a container owned by the
 * navigator and can't receive those props declaratively.
 */
function activateModalFocusTrap(
  dialogEl: HTMLElement | null,
  previousFocusRef: React.RefObject<Element | null>,
  applyDialogRole: boolean,
): () => void {
  previousFocusRef.current = document.activeElement;

  uninertOwnPortal(dialogEl);

  acquireScrollLock();

  let restoreDialogRole: (() => void) | undefined;
  if (applyDialogRole && dialogEl) {
    const prevRole = dialogEl.getAttribute("role");
    const prevAriaModal = dialogEl.getAttribute("aria-modal");
    dialogEl.setAttribute("role", "dialog");
    dialogEl.setAttribute("aria-modal", "true");
    restoreDialogRole = () => {
      if (prevRole === null) dialogEl.removeAttribute("role");
      else dialogEl.setAttribute("role", prevRole);
      if (prevAriaModal === null) dialogEl.removeAttribute("aria-modal");
      else dialogEl.setAttribute("aria-modal", prevAriaModal);
    };
  }

  // Move focus into the modal. Prefer the heading; fall back to the dialog
  // container. Either target may be a non-focusable element (a heading, or a
  // navigator-owned scene <div> with no tabindex), so make it programmatically
  // focusable for the duration of the trap and restore its state on cleanup —
  // otherwise `focus()` is a no-op and focus stays outside the modal.
  const heading = dialogEl?.querySelector?.('[role="heading"]') as HTMLElement | null;
  const focusTarget = heading ?? dialogEl;
  let restoreFocusTargetTabIndex: (() => void) | undefined;
  if (focusTarget) {
    if (focusTarget.tabIndex < 0 && !focusTarget.hasAttribute?.("tabindex")) {
      focusTarget.setAttribute("tabindex", "-1");
      restoreFocusTargetTabIndex = () => focusTarget.removeAttribute("tabindex");
    }
    focusTarget.focus?.();
  }

  const handleFocusIn = (e: FocusEvent) => {
    if (dialogEl && !dialogEl.contains(e.target as Node)) {
      e.stopPropagation();
      focusTarget?.focus?.();
    }
  };
  document.addEventListener("focusin", handleFocusIn);

  // `focusin` cannot catch focus escaping to browser chrome (URL bar).
  // Intercept Tab/Shift+Tab to cycle within the modal instead.
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key !== "Tab" || e.altKey || e.ctrlKey || e.metaKey || !dialogEl) return;
    const focusable = Array.from(dialogEl.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => !el.closest("[inert]"),
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };
  document.addEventListener("keydown", handleKeyDown);

  const inertedElements: HTMLElement[] = [];
  let node: HTMLElement | null = dialogEl?.parentElement ?? null;
  while (node && node !== document.body) {
    const parent: HTMLElement | null = node.parentElement;
    if (parent) {
      for (const sibling of Array.from(parent.children)) {
        if (sibling === node) continue;
        if (!(sibling as HTMLElement).inert) {
          (sibling as HTMLElement).inert = true;
          inertedElements.push(sibling as HTMLElement);
        }
      }
    }
    node = parent;
  }

  return () => {
    releaseScrollLock();
    document.removeEventListener("focusin", handleFocusIn);
    document.removeEventListener("keydown", handleKeyDown);
    for (const el of inertedElements) {
      el.inert = false;
    }
    restoreFocusTargetTabIndex?.();
    restoreDialogRole?.();
    const prev = previousFocusRef.current as HTMLElement | null;
    if (prev && document.contains(prev)) {
      prev.focus?.();
    }
    previousFocusRef.current = null;
  };
}

/**
 * Supplements React Native's Modal on web.
 *
 * On native, RNModal presents a separate OS-level screen that automatically
 * isolates focus, hides background content from assistive technology, and
 * restores focus on dismiss. On web the modal is a portal-rendered <div>,
 * so the browser has no built-in awareness of it. This hook fills that gap:
 *
 * 1. Moves focus into the modal heading (or the modal container) on open
 * 2. Traps keyboard focus so Tab cannot reach elements behind the backdrop
 * 3. Marks all DOM siblings as `inert` to hide them from screen readers
 * 4. Restores focus to the previously-focused element on close
 */
export function useModalFocusTrap(dialogRef: React.RefObject<object | null>, visible: boolean) {
  const previousFocusRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!visible) return;
    const dialogEl = dialogRef.current as unknown as HTMLElement | null;
    return activateModalFocusTrap(dialogEl, previousFocusRef, false);
  }, [visible, dialogRef]);
}

/**
 * Resolves the React Navigation scene container that wraps a screen's body.
 *
 * A stack scene renders as `sceneContainer > [header, sceneView > screenBody]`
 * (see `@react-navigation/stack` CardContainer). Given a ref to the screen body,
 * the container is two element levels up. The trap must operate on that
 * container — not the body — so the separately-slotted header (the close/back
 * button) stays inside the trap instead of being inerted as a sibling. Falls
 * back to the body element if the expected ancestors aren't present.
 */
function resolveSceneContainer(bodyEl: HTMLElement | null): HTMLElement | null {
  if (!bodyEl) return null;
  const sceneView = bodyEl.parentElement;
  const sceneContainer = sceneView?.parentElement ?? null;
  return sceneContainer ?? bodyEl;
}

/**
 * Focus trap for a modal presented as a React Navigation stack screen (e.g. a
 * `transparentModal`), where the header and body are rendered in separate
 * navigator slots rather than inside one dialog element.
 *
 * Unlike {@link useModalFocusTrap}, which takes a ref to the dialog element
 * directly, this takes a ref to the screen body and resolves the enclosing
 * scene container (header + body) as the dialog. The container is then given
 * `role="dialog"`/`aria-modal` for the duration of the trap.
 */
export function useSceneModalFocusTrap(bodyRef: React.RefObject<object | null>, visible: boolean) {
  const previousFocusRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!visible) return;
    const bodyEl = bodyRef.current as unknown as HTMLElement | null;
    const dialogEl = resolveSceneContainer(bodyEl);
    return activateModalFocusTrap(dialogEl, previousFocusRef, true);
  }, [visible, bodyRef]);
}

/** Closes the modal when the user presses Escape. */
export function useEscapeKey(onCloseRef: React.RefObject<(() => void) | null>, visible: boolean) {
  useEffect(() => {
    if (!visible) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current?.();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [visible, onCloseRef]);
}
